import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { 
  UserGroupIcon, 
  PlusIcon, 
  AcademicCapIcon,
  EyeIcon,
  PencilIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  IdentificationIcon,
  ArrowLeftIcon
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { getCompetencyDefinitions } from "../../../../api/hse/competency";
import { toast } from "react-hot-toast";

/**
 * HSE/KS Competency Definitions - Competency definitions list
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyDefinitions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    categories: [],
    types: [],
  });
  
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });

  useEffect(() => {
    fetchDefinitions();
  }, [filters]);

  const fetchDefinitions = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const apiFilters = {
        search: filters.query,
        category: filters.categories.length > 0 ? filters.categories[0] : undefined,
        type: filters.types.length > 0 ? filters.types[0] : undefined,
        is_active: true,
      };
      
      const response = await getCompetencyDefinitions(apiFilters);
      setDefinitions(response.competency_definitions || response.definitions || []);
    } catch (err) {
      console.error("Error fetching definitions:", err);
      setError(true);
      toast.error(t("Kunne ikke laste kompetansedefinisjoner"));
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters({
      query: newFilters.query || "",
      categories: newFilters.categories || [],
      types: newFilters.types || [],
    });
  };

  // Competency types for filtering
  const competencyTypes = [
    { value: "cv", label: t("CV"), icon: DocumentTextIcon },
    { value: "certificate", label: t("Sertifikat"), icon: DocumentCheckIcon },
    { value: "course", label: t("Kurs"), icon: AcademicCapIcon },
    { value: "license", label: t("Lisens"), icon: IdentificationIcon },
    { value: "other", label: t("Annet"), icon: DocumentTextIcon },
  ];

  // Category options for ModuleFilter
  const categoryOptions = [
    { value: "safety", label: t("Sikkerhet") },
    { value: "quality", label: t("Kvalitet") },
    { value: "environment", label: t("Miljø") },
    { value: "technical", label: t("Teknisk") },
    { value: "management", label: t("Ledelse") },
    { value: "other", label: t("Annet") },
  ];

  // Custom dropdown filters
  const customDropdownFilters = [
    {
      key: "categories",
      label: t("Kategori"),
      icon: UserGroupIcon,
      options: categoryOptions.map(cat => ({
        value: cat.value,
        label: cat.label,
        class: "bg-blue-100 text-blue-800",
      })),
    },
    {
      key: "types",
      label: t("Type"),
      icon: DocumentTextIcon,
      options: competencyTypes.map(type => ({
        value: type.value,
        label: type.label,
        class: "bg-purple-100 text-purple-800",
      })),
    },
  ];

  const headerActions = canManage ? [
    {
      label: t("Ny kompetanse"),
      icon: PlusIcon,
      path: "/dashboard/hse-ks/competency/definitions/new",
      hidden: false,
      menu: false,
    },
  ] : [];

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste kompetansedefinisjoner")]} />;
  }

  return (
    <DashboardHeader
      title={t("Kompetansedefinisjoner")}
      subtitle={t("Administrer kompetansedefinisjoner")}
      actions={headerActions}
      backButton={{
        label: t("Tilbake til kompetanse"),
        action: () => navigate("/dashboard/hse-ks/competency"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          {/* ModuleFilter */}
          <ModuleFilter
            onFiltersChange={handleFiltersChange}
            initialFilters={filters}
            searchPlaceholder={t("Søk i kompetansedefinisjoner...")}
            customDropdownFilters={customDropdownFilters}
          />

          {/* Definitions List */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("Kompetansedefinisjoner")} ({definitions.length})
                </h3>
              </div>
              
              {definitions.length === 0 ? (
                <ResourceEmpty message={t("Ingen kompetansedefinisjoner funnet")} />
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t("Kompetanse")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Kategori")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Ansatte")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Status")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {definitions.map((definition) => (
                        <tr key={definition.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              {definition.type === "cv" && <DocumentTextIcon className="h-5 w-5 text-blue-600 mr-3" />}
                              {definition.type === "certificate" && <DocumentCheckIcon className="h-5 w-5 text-green-600 mr-3" />}
                              {definition.type === "course" && <AcademicCapIcon className="h-5 w-5 text-purple-600 mr-3" />}
                              {definition.type === "license" && <IdentificationIcon className="h-5 w-5 text-orange-600 mr-3" />}
                              {(!definition.type || definition.type === "other") && <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-3" />}
                              <div>
                                <div className="font-medium text-gray-900">{definition.name}</div>
                                {definition.description && (
                                  <div className="text-gray-500 text-xs mt-1 line-clamp-2">
                                    {definition.description}
                                  </div>
                                )}
                                {definition.type && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                                    {competencyTypes.find(t => t.value === definition.type)?.label || definition.type}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {definition.category || t("Ikke kategorisert")}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <UserGroupIcon className="h-4 w-4 mr-1" />
                              {definition.employee_count || 0}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            {definition.is_active ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                {t("Aktiv")}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {t("Inaktiv")}
                              </span>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex items-center justify-end space-x-2">
                              <Link
                                to={`/dashboard/hse-ks/competency/definitions/${definition.id}`}
                                className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                                title={t("Vis")}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Link>
                              {canManage && (
                                <Link
                                  to={`/dashboard/hse-ks/competency/definitions/${definition.id}/edit`}
                                  className="text-gray-600 hover:text-gray-900 inline-flex items-center"
                                  title={t("Rediger")}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardHeader>
  );
};

